import { useCallback, useEffect, useRef } from 'react';
import { onWebSocketReconnect } from './websocketConnection';

const DEFAULT_INTERVAL_MS = 90_000;

/**
 * Websocket events are emitted as soon as the model changes, but the counter endpoints
 * only reflect it after the transaction settles. Trailing the sync by a short delay also
 * collapses bursts of events into a single request.
 */
const DEFAULT_COALESCE_MS = 700;

interface UseCounterSyncOptions {
  enabled?: boolean;
  intervalMs?: number;
  coalesceMs?: number;
}

/**
 * Keeps a counter honest even when websocket events are missed.
 *
 * Deltas pushed over the websocket are the fast path, never the source of truth: the tab
 * can be asleep, the socket can drop, or an event can simply not be broadcast. This hook
 * re-runs `onSync` on mount, when the tab becomes visible again, when the browser comes
 * back online, when the websocket reconnects and on a slow safety-net interval.
 *
 * It returns the same coalesced `sync` callback used by those triggers so websocket
 * handlers can reuse it instead of firing their own requests.
 */
export default function useCounterSync(
  onSync: () => void,
  { enabled = true, intervalMs = DEFAULT_INTERVAL_MS, coalesceMs = DEFAULT_COALESCE_MS }: UseCounterSyncOptions = {},
) {
  const onSyncRef = useRef(onSync);
  const enabledRef = useRef(enabled);
  const timerRef = useRef<number>();

  onSyncRef.current = onSync;
  enabledRef.current = enabled;

  const sync = useCallback(() => {
    if (!enabledRef.current || timerRef.current) {
      return;
    }

    timerRef.current = window.setTimeout(() => {
      timerRef.current = undefined;
      onSyncRef.current();
    }, coalesceMs);
  }, [coalesceMs]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const syncWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        sync();
      }
    };

    sync();

    const interval = window.setInterval(syncWhenVisible, intervalMs);
    const unsubscribeReconnect = onWebSocketReconnect(sync);

    window.addEventListener('focus', syncWhenVisible);
    window.addEventListener('online', syncWhenVisible);
    document.addEventListener('visibilitychange', syncWhenVisible);

    return () => {
      window.clearInterval(interval);
      unsubscribeReconnect();
      window.removeEventListener('focus', syncWhenVisible);
      window.removeEventListener('online', syncWhenVisible);
      document.removeEventListener('visibilitychange', syncWhenVisible);
    };
  }, [enabled, intervalMs, sync]);

  useEffect(() => {
    return () => {
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    };
  }, []);

  return sync;
}
